import React from "react";
import {
  Paper,
  Typography,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import SupplyOrderAttachmentsTable from "./SupplyOrderAttachmentsTable";
import SupplyOrderHistoryTable from "./SupplyOrderHistoryTable";

const ViewSupplyOrder = ({ order }) => {
  if (!order) {
    return null;
  }

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString() : "";

  const formatMoney = (value) =>
    value !== null && value !== undefined ? `$${Number(value).toFixed(2)}` : "";

  const balanceDue =
    (Number(order.totalAmount) || 0) - (Number(order.amountPaid) || 0);

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Supply Order {order.poNumber}{" "}
        <Chip label={order.status} size="small" color="primary" />
      </Typography>
      <Divider sx={{ mb: 1.5 }} />

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">Vendor: {order.vendorName}</Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">Priority: {order.priority}</Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">Order Type: {order.orderType}</Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Order Date: {formatDate(order.orderDate)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Expected Date: {formatDate(order.expectedDate)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Must Ship By: {formatDate(order.mustShipByDate)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Received Date: {formatDate(order.receivedDate)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Tracking Number: {order.trackingNumber}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">Carrier: {order.carrier}</Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Vendor Invoice #: {order.vendorInvoiceNumber}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Shipping Cost: {formatMoney(order.shippingCost)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Order Total: {formatMoney(order.totalAmount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Deposit Amount: {formatMoney(order.depositAmount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Amount Paid: {formatMoney(order.amountPaid)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Balance Due: {formatMoney(balanceDue)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Typography variant="body2">
            Approved By: {order.approvedBy}{" "}
            {order.approvedAt ? `(${formatDate(order.approvedAt)})` : ""}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2">Notes: {order.notes}</Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Order Lines
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Product</TableCell>
            <TableCell>Item Number</TableCell>
            <TableCell>Variant</TableCell>
            <TableCell>Ordered</TableCell>
            <TableCell>Received</TableCell>
            <TableCell>Order Level</TableCell>
            <TableCell>Unit Cost</TableCell>
            <TableCell>Line Total</TableCell>
            <TableCell>Received Into</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(order.items || []).map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.lineNumber}</TableCell>
              <TableCell>{item.productName}</TableCell>
              <TableCell>{item.sku}</TableCell>
              <TableCell>{item.variantName}</TableCell>
              <TableCell>{item.quantityOrdered}</TableCell>
              <TableCell>{item.quantityReceived}</TableCell>
              <TableCell>{item.orderLevel}</TableCell>
              <TableCell>{formatMoney(item.unitCost)}</TableCell>
              <TableCell>{formatMoney(item.lineTotal)}</TableCell>
              <TableCell>{item.receiveLocationName}</TableCell>
              <TableCell>{item.notes}</TableCell>
            </TableRow>
          ))}
          {(order.items || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={11}>
                <Typography variant="body2" color="textSecondary">
                  No lines on this order
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <SupplyOrderAttachmentsTable poNumber={order.poNumber} readOnly />
      <SupplyOrderHistoryTable history={order.history || []} />
    </Paper>
  );
};

export default ViewSupplyOrder;
